import { FunctionComponent } from 'react';
import {
  Dashboard,
  MasterImportNewExecution,
  MastersImport,
  FiscalDetail,
  ConciliationDetail,
  ConciliationHeader,
  HelpCenter,
  Profile,
  SignIn,
  Http403,
  Http404,
} from '../pages';
import { AppPermissions } from '../types';
import Reprocess from '../pages/Reprocess';
import ReprocessNew from '../pages/ReprocessNew';
import SynchroForMe from '../pages/SynchroForMe';
import SynchroExecutionNew from '../pages/SynchroExecutionNew';
import ManagementReport from '../pages/ManagementReport';
import ParametricStores from '../pages/ParametricStores';

export interface RouteConfig {
  key: string;
  path: string;
  component: FunctionComponent;
  modeModal: boolean;
}

interface ProtectedRouteConfig extends RouteConfig {
  permissions: AppPermissions[];
  defaultRedirectForPermission?: AppPermissions;
}

const getRouteConfig = (
  key: string,
  path: string,
  component: FunctionComponent,
  modeModal = false
): RouteConfig => ({
  key,
  path,
  component,
  modeModal,
});

const getProtectedRouteConfig = (
  key: string,
  path: string,
  component: FunctionComponent,
  defaultRedirectForPermission: AppPermissions | undefined = undefined,
  modeModal = false,
  permissions: AppPermissions[] = []
): ProtectedRouteConfig => ({
  ...getRouteConfig(key, path, component, modeModal),
  permissions,
  defaultRedirectForPermission,
});

const protectedRoutes = {
  dashboard: getProtectedRouteConfig(
    'dashboard',
    '/dashboard',
    Dashboard,
    AppPermissions.CONCILIATIONS_DASHBOARD,
    false,
    [AppPermissions.CONCILIATIONS_DASHBOARD]
  ),
  conciliationDetail: getProtectedRouteConfig(
    'conciliation-detail',
    '/conciliation-detail',
    ConciliationDetail,
    undefined,
    false,
    [AppPermissions.CONCILIATIONS_DETAILS]
  ),
  conciliationHeader: getProtectedRouteConfig(
    'conciliation-header',
    '/conciliation-header',
    ConciliationHeader,
    undefined,
    false,
    [AppPermissions.CONCILIATIONS_HEADER]
  ),
  profile: getProtectedRouteConfig('profile', '/profile', Profile, undefined, true, [
    AppPermissions.CONCILIATIONS_DASHBOARD,
  ]),
  synchroForMe: getProtectedRouteConfig(
    'synchro-for-me',
    '/synchro-for-me',
    SynchroForMe,
    undefined,
    false,
    [AppPermissions.SYNCHRO_4_ME]
  ),
  managementReport: getProtectedRouteConfig(
    'management-report',
    '/management-report',
    ManagementReport,
    undefined,
    false,
    [AppPermissions.CLOSING_DAYS_REPORT]
  ),
  parametricStores: getProtectedRouteConfig(
    'parametric-stores',
    '/parametric-stores',
    ParametricStores,
    undefined,
    false,
    [AppPermissions.PARAMETRIC_STORES]
  ),
  helpCenter: getProtectedRouteConfig('help-center', '/help-center', HelpCenter),
};

const protectedNestedRoutes = {
  mastersImport: {
    index: getProtectedRouteConfig(
      'masters-import',
      '/masters-import',
      MastersImport,
      undefined,
      false,
      [AppPermissions.DASHBOARD_MASTER_IMPORT]
    ),
    children: {
      create: getProtectedRouteConfig(
        'masterImportNewExecution',
        'new',
        MasterImportNewExecution,
        undefined,
        false,
        [AppPermissions.DASHBOARD_MASTER_IMPORT]
      ),
    },
  },
  fiscalDetail: {
    index: getProtectedRouteConfig(
      'fiscal-detail',
      '/fiscal-detail',
      FiscalDetail,
      undefined,
      false,
      [AppPermissions.DASHBOARD_FISCAL_IMPORT]
    ),
    children: {
      create: getProtectedRouteConfig('reprocessNew', 'new', ReprocessNew, undefined, false, [
        AppPermissions.REPROCESS,
      ]),
    },
  },
  reprocess: {
    index: getProtectedRouteConfig('reprocess', '/reprocess', Reprocess, undefined, false, [
      AppPermissions.REPROCESS,
    ]),
    children: {
      create: getProtectedRouteConfig('reprocessNew', 'new', ReprocessNew, undefined, false, [
        AppPermissions.REPROCESS,
      ]),
    },
  },
  synchro: {
    index: getProtectedRouteConfig(
      'synchro-for-me',
      '/synchro-for-me',
      SynchroForMe,
      undefined,
      false,
      [AppPermissions.SYNCHRO_4_ME]
    ),
    children: {
      create: getProtectedRouteConfig('synchroNew', 'new', SynchroExecutionNew, undefined, false, [
        AppPermissions.SYNCHRO_4_ME,
      ]),
    },
  },
};

const signinRoute = getRouteConfig('signin', '/signin', SignIn);

export default {
  protectedRoutes,
  protectedNestedRoutes,
  signinRoute,
  forbiddenComponent: Http403,
  notFoundComponent: Http404,
};
